import React, { useState, useCallback, useRef, useEffect } from 'react';

import { useAppContext } from '@/hooks/use-app-context';

export interface LodControlProps {
  /** スタイルのカスタマイズ（オプション） */
  style?: React.CSSProperties;
}

/** 品質プリセット */
type QualityPreset = 'ultra' | 'standard' | 'light' | 'minimal' | 'custom';

/** プリセット値の定義 */
const QUALITY_PRESETS = {
  ultra: { value: 2.0, label: '高品質' },
  standard: { value: 1.0, label: '標準' },
  light: { value: 0.5, label: '軽量' },
  minimal: { value: 0.1, label: '最軽量' }
} as const;

/**
 * LODスプラットスケール調整コンポーネント
 *
 * 右下に配置され、点群表示数を調整するスライダーと現在の点群数情報を表示します。
 */
export function LodControl({ style }: LodControlProps) {
  const { state, dispatch } = useAppContext();
  const { lodSplatScale, pointCloudMetrics, isLoading } = state;

  // スロットリング用
  const [localValue, setLocalValue] = useState(lodSplatScale);
  const [selectedPreset, setSelectedPreset] = useState<QualityPreset>('custom');
  const [showAdvanced, setShowAdvanced] = useState(false);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);

  // プリセット選択ハンドラー
  const handlePresetChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const preset = e.target.value as Exclude<QualityPreset, 'custom'>;
      setSelectedPreset(preset);

      const presetValue = QUALITY_PRESETS[preset].value;
      setLocalValue(presetValue);
      dispatch({ type: 'SET_LOD_SPLAT_SCALE', payload: presetValue });
    },
    [dispatch]
  );

  // スライダー変更ハンドラー（スロットリング付き）
  const handleSliderChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = parseFloat(e.target.value);
      setLocalValue(newValue);
      setSelectedPreset('custom'); // スライダー操作時はカスタムモードに

      // 既存のタイマーをクリア
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      // 300ms後に状態更新
      timeoutRef.current = setTimeout(() => {
        dispatch({ type: 'SET_LOD_SPLAT_SCALE', payload: newValue });
      }, 300);
    },
    [dispatch]
  );

  // クリーンアップ
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // 外部からのlodSplatScale変更に対応
  useEffect(() => {
    setLocalValue(lodSplatScale);

    // プリセット値と一致するか確認
    const matchedPreset = Object.entries(QUALITY_PRESETS).find(
      ([, preset]) => Math.abs(preset.value - lodSplatScale) < 0.01
    );

    if (matchedPreset) {
      setSelectedPreset(matchedPreset[0] as QualityPreset);
    } else {
      setSelectedPreset('custom');
    }
  }, [lodSplatScale]);

  // 点群数のフォーマット（万単位表示）
  const formatNumber = (num: number): string => {
    if (num >= 10000) {
      const man = Math.floor(num / 10000);
      return `${man}万`;
    }
    return num.toLocaleString('ja-JP');
  };

  // 正確な点群数のフォーマット（カンマ区切り）
  const formatExactNumber = (num: number): string => {
    return num.toLocaleString('ja-JP');
  };

  return (
    <div
      style={{
        position: 'absolute',
        bottom: '32px',
        right: '88px',
        width: '280px',
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        borderRadius: '8px',
        padding: '16px',
        color: '#333',
        zIndex: 1002,
        fontFamily: 'sans-serif',
        fontSize: '14px',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.3)',
        ...style
      }}
    >
      {/* 説明文 */}
      <div
        style={{ fontSize: '12px', color: '#333', lineHeight: '1.6', fontFamily: 'sans-serif', marginBottom: '16px' }}
      >
        デバイスでの動作が重い場合は品質を下げてください。ただし、表示品質が下がります。
      </div>

      {/* プリセット選択 */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ marginBottom: '4px' }}>
          <span style={{ fontSize: '12px', color: '#333', fontFamily: 'sans-serif' }}>品質</span>
        </div>
        <div style={{ position: 'relative' }}>
          <select
            value={selectedPreset === 'custom' ? 'standard' : selectedPreset}
            style={{
              width: '100%',
              padding: '8px',
              borderRadius: '4px',
              border: '1px solid rgba(0, 0, 0, 0.2)',
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              color: '#333',
              fontSize: '14px',
              fontFamily: 'sans-serif',
              cursor: 'pointer'
            }}
            onChange={handlePresetChange}
          >
            <option value='ultra'>{QUALITY_PRESETS.ultra.label}（200%）</option>
            <option value='standard'>{QUALITY_PRESETS.standard.label}（100%）</option>
            <option value='light'>{QUALITY_PRESETS.light.label}（50%）</option>
            <option value='minimal'>{QUALITY_PRESETS.minimal.label}（10%）</option>
          </select>
          {selectedPreset === 'custom' && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                padding: '8px',
                borderRadius: '4px',
                border: '1px solid rgba(0, 0, 0, 0.2)',
                backgroundColor: 'rgba(255, 255, 255, 0.9)',
                color: '#333',
                fontSize: '14px',
                fontFamily: 'sans-serif',
                pointerEvents: 'none',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <span>カスタム（{(localValue * 100).toFixed(0)}%）</span>
              <span style={{ fontSize: '12px' }}>▼</span>
            </div>
          )}
        </div>
      </div>

      {/* 詳細設定（スライダー） */}
      <div style={{ marginBottom: '16px' }}>
        <button
          type='button'
          style={{
            width: '100%',
            padding: '6px',
            backgroundColor: 'transparent',
            border: 'none',
            color: '#333',
            fontSize: '12px',
            fontFamily: 'sans-serif',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            gap: '4px'
          }}
          onClick={() => setShowAdvanced(!showAdvanced)}
        >
          <span>{showAdvanced ? '▼' : '▶'}</span>
          <span>詳細</span>
        </button>
        {showAdvanced && (
          <div style={{ marginTop: '8px', paddingLeft: '20px' }}>
            <div
              style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px', alignItems: 'center' }}
            >
              <span style={{ fontSize: '12px', color: '#333', fontFamily: 'sans-serif' }}>カスタム値</span>
              <span style={{ fontSize: '14px', fontWeight: 'bold', color: '#333', fontFamily: 'sans-serif' }}>
                {(localValue * 100).toFixed(0)}%
              </span>
            </div>
            <input
              max='2.0'
              min='0.01'
              step='0.01'
              type='range'
              value={localValue}
              style={{
                width: '100%',
                height: '4px',
                cursor: 'pointer',
                accentColor: '#22A9BC'
              }}
              onChange={handleSliderChange}
            />

            {/* 点群数情報 */}
            <div style={{ fontSize: '12px', color: '#333', marginTop: '12px', fontFamily: 'sans-serif' }}>
              {isLoading ? (
                <div>読み込み中...</div>
              ) : pointCloudMetrics.isLoaded ? (
                <div>
                  <div
                    style={{ marginBottom: '4px' }}
                    title={`${formatExactNumber(pointCloudMetrics.estimatedActiveSplats)}点`}
                  >
                    表示:{' '}
                    <span style={{ fontWeight: 'bold' }}>
                      {formatNumber(pointCloudMetrics.estimatedActiveSplats)}点群
                    </span>
                  </div>
                  <div title={`${formatExactNumber(pointCloudMetrics.originalSplats)}点`}>
                    オリジナル:{' '}
                    <span style={{ fontWeight: 'bold' }}>{formatNumber(pointCloudMetrics.originalSplats)}点群</span>
                  </div>
                </div>
              ) : (
                <div style={{ color: 'rgba(51, 51, 51, 0.6)' }}>データ未読み込み</div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
